import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useHttp from '../../hooks/http.hook'
import FullscreenSpinner from '../components/FullscreenSpinner'
import OrderDetailPdfCard from './components/OrderDetailPdfCard'
import OrderPayQrModal from './components/OrderPayQrModal'
import { formatPeriodRange } from '../../utils/rentalPeriod'
import './OrderDetailPage.css'

const SHIPMENT_PRODUCT_ID = -10

const ORDER_PAID = 2

const PAYMENT_STATUS_NAMES = {
  1: 'Не оплачен',
  [ORDER_PAID]: 'Оплачен',
  3: 'Частично оплачен',
}

function formatOrderDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  const date = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d).replace(/\s*г\.\s*$/, '')
  const time = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
  return `${date}, ${time}`
}

function isShipmentItem(item) {
  const productId = item?.productId ?? item?.product_id
  return Number(productId) === SHIPMENT_PRODUCT_ID
}

function branchMapUrl(branch) {
  const lng = branch?.location?.lng
  const lat = branch?.location?.lat
  if (typeof lng !== 'number' || typeof lat !== 'number') return null
  return `https://2gis.ru/geo/${lng},${lat}`
}

function buildStatusTrack(statuses, currentStatusId, paymentStatusId) {
  const list = Array.isArray(statuses) ? statuses : []
  const currentIndex = list.findIndex((status) => Number(status.id) === Number(currentStatusId))
  const isPaid = Number(paymentStatusId) === ORDER_PAID
  const paymentIsFocus = !isPaid && currentIndex === 0
  const paymentState = isPaid ? 'done' : paymentIsFocus ? 'current' : 'upcoming'

  const steps = [
    { key: 'payment', name: 'Оплата', state: paymentState, payment: true },
    ...list.map((status, index) => {
      let state = 'upcoming'
      if (currentIndex >= 0 && !paymentIsFocus) {
        if (index < currentIndex) state = 'done'
        else if (index === currentIndex) state = 'current'
      }
      return {
        key: `status-${status.id}`,
        name: status.name,
        state,
        payment: false,
      }
    }),
  ]

  return steps.map((step, index) => ({
    ...step,
    lineBefore: index > 0 && steps[index - 1].state === 'done',
    lineAfter: index < steps.length - 1 && step.state === 'done',
  }))
}

function StepDot({ state, payment }) {
  const className = [
    'client-order-step-dot',
    `client-order-step-dot--${state}`,
    payment ? 'client-order-step-dot--payment' : '',
  ].filter(Boolean).join(' ')

  if (state === 'done') {
    return (
      <span className={className} aria-hidden>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <circle cx="9" cy="9" r="7" fill="currentColor" stroke="currentColor" strokeWidth="1.5" />
          <path d="M5.4 9.15 L7.9 11.45 L12.6 6.55" stroke="var(--theme-page-bg-alt)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    )
  }

  return (
    <span className={className} aria-hidden>
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle
          cx="9"
          cy="9"
          r={state === 'current' ? 7 : 7.05}
          fill={state === 'current' ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    </span>
  )
}

function OrderStatusStepper({ statuses, currentStatusId, paymentStatusId }) {
  const scrollerRef = useRef(null)
  const currentRef = useRef(null)
  const steps = useMemo(
    () => buildStatusTrack(statuses, currentStatusId, paymentStatusId),
    [statuses, currentStatusId, paymentStatusId]
  )

  useEffect(() => {
    const scroller = scrollerRef.current
    const current = currentRef.current
    if (!scroller) return
    const updateShadow = () => {
      const distance = Math.max(0, scroller.scrollLeft)
      const amount = Math.min(1, distance / 56)
      scroller.style.setProperty('--payment-shadow', amount.toFixed(3))
    }
    let cancelled = false
    let animFrame = 0
    const align = () => {
      if (cancelled) return
      scroller.scrollTop = 0
      const maxScroll = scroller.scrollWidth - scroller.clientWidth
      let target = 0
      if (current && maxScroll > 0) {
        const centered = current.offsetLeft - (scroller.clientWidth - current.offsetWidth) / 2
        target = Math.max(0, Math.min(maxScroll, centered))
      }
      const from = scroller.scrollLeft
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (reduced || Math.abs(target - from) < 1) {
        scroller.scrollLeft = target
        updateShadow()
        return
      }
      const duration = 1100
      const start = performance.now()
      const tick = (now) => {
        if (cancelled) return
        const t = Math.min(1, (now - start) / duration)
        const eased = 1 - (1 - t) ** 3
        scroller.scrollLeft = from + (target - from) * eased
        if (t < 1) animFrame = requestAnimationFrame(tick)
      }
      animFrame = requestAnimationFrame(tick)
    }
    // Wait a frame so step widths are measured before the slow scroll starts.
    const frame = requestAnimationFrame(() => requestAnimationFrame(align))
    scroller.addEventListener('scroll', updateShadow, { passive: true })
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
      cancelAnimationFrame(animFrame)
      scroller.removeEventListener('scroll', updateShadow)
    }
  }, [steps])

  return (
    <div className="client-order-stepper">
      <div className="client-order-stepper-scroll" ref={scrollerRef}>
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1
          const className = [
            'client-order-step',
            `client-order-step--${step.state}`,
            step.payment ? 'client-order-step--payment' : '',
          ].filter(Boolean).join(' ')
          return (
            <div
              key={step.key}
              className={className}
              ref={step.state === 'current' ? currentRef : null}
            >
              <div className="client-order-step-rail">
                {index === 0 ? (
                  <span className="client-order-step-spacer" />
                ) : (
                  <span className={`client-order-step-line${step.lineBefore ? ' client-order-step-line--filled' : ''}`} />
                )}
                <StepDot state={step.state} payment={step.payment} />
                {isLast ? (
                  <span className="client-order-step-spacer" />
                ) : (
                  <span className={`client-order-step-line${step.lineAfter ? ' client-order-step-line--filled' : ''}`} />
                )}
              </div>
              <span className="client-order-step-label">{step.name}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function HeaderBackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M19 12H6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M11 6L5 12L11 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function OrderDetailPage() {
  const { business, orderId } = useParams()
  const navigate = useNavigate()
  const { requestWithMeta } = useHttp()
  const pdfRef = useRef(null)
  const copyTimerRef = useRef(null)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hasPaymentProvider, setHasPaymentProvider] = useState(false)
  const [payBusy, setPayBusy] = useState(false)
  const [payError, setPayError] = useState(null)
  const [payModalOpen, setPayModalOpen] = useState(false)
  const [paySession, setPaySession] = useState(null)
  const [copied, setCopied] = useState(false)
  const [captureBusy, setCaptureBusy] = useState(false)

  const fetchOrder = useCallback(async () => {
    if (!business || !orderId) return
    setLoading(true)
    setError(null)
    try {
      const { ok, data: res } = await requestWithMeta(
        `/business/${encodeURIComponent(business)}/orders/${encodeURIComponent(orderId)}`,
        'GET'
      )
      if (!ok) {
        setError(res?.error || 'Order not found')
        setData(null)
        setHasPaymentProvider(false)
        return
      }
      setData(res)
      setHasPaymentProvider(Boolean(res?.hasPaymentProvider))
    } catch (e) {
      setError(e.message || 'Failed to load order')
      setData(null)
      setHasPaymentProvider(false)
    } finally {
      setLoading(false)
    }
  }, [business, orderId, requestWithMeta])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  useEffect(() => () => clearTimeout(copyTimerRef.current), [])

  const applyPaymentSnapshot = useCallback((snapshot) => {
    if (snapshot?.paymentStatusId == null && snapshot?.debt == null) return
    setData((prev) => {
      if (!prev?.order) return prev
      const paymentStatusId = snapshot.paymentStatusId ?? prev.order.paymentStatusId
      return {
        ...prev,
        order: {
          ...prev.order,
          paymentStatusId,
          payment_status_id: paymentStatusId,
          paymentStatusName: PAYMENT_STATUS_NAMES[paymentStatusId] ?? prev.order.paymentStatusName,
          debt: snapshot.debt ?? prev.order.debt,
        },
      }
    })
  }, [])

  const handlePay = useCallback(async () => {
    if (!business || !orderId || payBusy) return
    setPayBusy(true)
    setPayError(null)
    try {
      const { ok, data: res } = await requestWithMeta(
        `/business/${encodeURIComponent(business)}/orders/${encodeURIComponent(orderId)}/pay`,
        'POST'
      )
      if (!ok || !res?.payment) {
        setPayError(res?.error || 'Не удалось создать оплату')
        return
      }
      setPaySession({ payment: res.payment, invoice: res.invoice ?? null })
      applyPaymentSnapshot(res)
      setPayModalOpen(true)
    } catch (e) {
      setPayError(e.message || 'Не удалось создать оплату')
    } finally {
      setPayBusy(false)
    }
  }, [business, orderId, payBusy, requestWithMeta, applyPaymentSnapshot])

  const handlePollPayment = useCallback(async (paymentId) => {
    if (!business || !orderId || paymentId == null) return null
    try {
      const { ok, data: res } = await requestWithMeta(
        `/business/${encodeURIComponent(business)}/orders/${encodeURIComponent(orderId)}/payments/${encodeURIComponent(paymentId)}/status`,
        'GET'
      )
      if (!ok || !res?.payment) return null
      return res
    } catch {
      return null
    }
  }, [business, orderId, requestWithMeta])

  const goToBusiness = useCallback(() => {
    if (business) {
      navigate(`/${encodeURIComponent(business)}`)
      return
    }
    navigate('/')
  }, [navigate, business])

  const copyPageLink = useCallback(async () => {
    const url = window.location.href
    let copiedOk = false
    try {
      await navigator.clipboard.writeText(url)
      copiedOk = true
    } catch {
      try {
        const input = document.createElement('textarea')
        input.value = url
        input.setAttribute('readonly', '')
        input.style.position = 'fixed'
        input.style.left = '-9999px'
        document.body.appendChild(input)
        input.select()
        copiedOk = document.execCommand('copy')
        document.body.removeChild(input)
      } catch {
        copiedOk = false
      }
    }
    if (!copiedOk) return
    setCopied(true)
    clearTimeout(copyTimerRef.current)
    copyTimerRef.current = setTimeout(() => setCopied(false), 1500)
  }, [])

  const orderedItems = useMemo(() => {
    const items = Array.isArray(data?.stockMovements) ? data.stockMovements : []
    const shipments = []
    const products = []
    for (const item of items) {
      if (isShipmentItem(item)) shipments.push(item)
      else products.push(item)
    }
    return [...shipments, ...products]
  }, [data?.stockMovements])

  if (loading) {
    return <FullscreenSpinner open label="Загрузка заказа…" />
  }

  if (error || !data) {
    return (
      <div className="client-order-detail-page">
        <div className="client-order-detail-top">
          <div className="client-order-detail-header">
            <button type="button" className="client-order-detail-back" onClick={goToBusiness} aria-label="Назад в бизнес">
              <HeaderBackIcon />
            </button>
            <div className="client-order-detail-heading">
              <div className="client-order-detail-titleRow">
                <h1 className="client-order-detail-title">Заказ</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="client-order-detail-content">
          <div className="client-order-detail-card client-order-detail-error-card">
            {error}
          </div>
        </div>
      </div>
    )
  }

  const { order, branch, stockMovements, orderStatuses, currentStatusId } = data
  const total = orderedItems.reduce(
    (sum, it) => sum + Number(it.quantity || 0) * parseFloat(it.productPrice || 0),
    0
  )
  const paymentStatusId = Number(order.paymentStatusId ?? order.payment_status_id)
  const showPay = hasPaymentProvider && paymentStatusId !== ORDER_PAID
  const mapUrl = branchMapUrl(branch)

  return (
    <div className="client-order-detail-page">
      <div className="client-order-detail-top">
        <div className="client-order-detail-header">
          <button type="button" className="client-order-detail-back" onClick={goToBusiness} aria-label="Назад в бизнес">
            <HeaderBackIcon />
          </button>
          <div className="client-order-detail-heading">
            <div className="client-order-detail-titleRow">
              <h1 className="client-order-detail-title">Заказ №{order.id}</h1>
              <button
                type="button"
                className="client-order-detail-copy"
                onClick={copyPageLink}
                aria-label={copied ? 'Ссылка скопирована' : 'Скопировать ссылку'}
              >
                {copied ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M5 12.5 L10 17.5 L19 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <rect x="9" y="8" width="10" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M15 8V6.5A1.5 1.5 0 0 0 13.5 5h-7A1.5 1.5 0 0 0 5 6.5v9A1.5 1.5 0 0 0 6.5 17H8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                )}
              </button>
            </div>
            <div className="client-order-detail-dateLine">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
                <path d="M12 8v4.5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{formatOrderDate(order.createdAt)}</span>
            </div>
          </div>
        </div>

        <OrderStatusStepper
          statuses={orderStatuses}
          currentStatusId={currentStatusId ?? order.statusId ?? order.status_id}
          paymentStatusId={paymentStatusId}
        />
      </div>

      <div className="client-order-detail-content">
        <div className="client-order-detail-card">
          <section className="client-order-detail-section">
            <h3 className="client-order-detail-section-title">Состав заказа</h3>
            <ul className="client-order-detail-items">
              {orderedItems.map((it) => {
                const isShipment = isShipmentItem(it)
                return (
                  <li
                    key={it.id}
                    className={`client-order-detail-item${isShipment ? ' client-order-detail-item--shipment' : ''}`}
                  >
                    <span className="client-order-detail-item-name">
                      {isShipment ? (
                        <span className="client-order-detail-shipmentPrefix">Доставка: </span>
                      ) : null}
                      {it.productName}
                      {(it.fromAt || it.from_at) ? (
                        <span className="client-order-detail-item-period">
                          {formatPeriodRange(it.fromAt ?? it.from_at, it.toAt ?? it.to_at)}
                        </span>
                      ) : null}
                    </span>
                    {!isShipment ? (
                      <span className="client-order-detail-item-qty">×{it.quantity}</span>
                    ) : null}
                    <span className="client-order-detail-item-price">
                      {parseFloat(it.productPrice || 0).toFixed(2)} сом
                    </span>
                  </li>
                )
              })}
            </ul>
            <div className="client-order-detail-total">
              <span>Итого</span>
              <span>{total.toFixed(2)} сом</span>
            </div>
          </section>

          <section className="client-order-detail-section">
            <h3 className="client-order-detail-section-title">Клиент</h3>
            <div className="client-order-detail-row">
              <span className="client-order-detail-label">Имя</span>
              <span className="client-order-detail-value">{order.customerName || '—'}</span>
            </div>
            <div className="client-order-detail-row">
              <span className="client-order-detail-label">Телефон</span>
              <a href={`tel:${order.customerPhone || ''}`} className="client-order-detail-value client-order-detail-link">
                {order.customerPhone || '—'}
              </a>
            </div>
            {order.customerDescription && (
              <div className="client-order-detail-row">
                <span className="client-order-detail-label">Комментарий</span>
                <span className="client-order-detail-value">{order.customerDescription}</span>
              </div>
            )}
          </section>

          <div className="client-order-detail-iconRow">
            <span className="client-order-detail-branchName">{branch?.name || '—'}</span>
            <div className="client-order-detail-iconBtns">
            {mapUrl ? (
              <a
                className="client-order-detail-iconBtn"
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Открыть в 2ГИС"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  <circle cx="12" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.8" />
                </svg>
              </a>
            ) : (
              <span className="client-order-detail-iconBtn" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  <circle cx="12" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.8" />
                </svg>
              </span>
            )}
            <button
              type="button"
              className="client-order-detail-iconBtn"
              onClick={() => pdfRef.current?.save()}
              disabled={captureBusy}
              aria-label="Сохранить"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M12 4v10m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              className="client-order-detail-iconBtn"
              onClick={() => pdfRef.current?.share()}
              disabled={captureBusy}
              aria-label="Поделиться"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="6" cy="12" r="2.1" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="17" cy="7" r="2.1" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="17" cy="17" r="2.1" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8 11.1 L14.8 8.1 M8 12.9 L14.8 15.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            </div>
          </div>
        </div>

        <OrderDetailPdfCard
          ref={pdfRef}
          order={order}
          branch={branch}
          stockMovements={stockMovements}
          onBusyChange={setCaptureBusy}
        />

        <div className="client-order-detail-cta">
          {showPay ? (
            <button
              type="button"
              className="client-order-detail-btn client-order-detail-btn--primary"
              onClick={handlePay}
              disabled={payBusy}
            >
              {payBusy ? 'Создание…' : 'Оплатить'}
            </button>
          ) : (
            <button type="button" className="client-order-detail-backToShop" onClick={goToBusiness}>
              <BackIcon />
              Назад в каталог
            </button>
          )}
          {payError ? <div className="client-order-pay-error">{payError}</div> : null}
        </div>

        <OrderPayQrModal
          open={payModalOpen}
          payment={paySession?.payment}
          invoice={paySession?.invoice}
          orderId={order.id}
          onClose={() => setPayModalOpen(false)}
          onPoll={handlePollPayment}
          onSnapshot={applyPaymentSnapshot}
        />
      </div>
    </div>
  )
}

export default OrderDetailPage
